import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, catchError, tap, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';
import { StorageService } from '../../_services/storage/storage.service';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/services/auth/auth.service';
import { Navigation } from 'src/app/_utlis/navigateToLanding';
import { ToastService } from 'src/app/Shared/services/toast/toast.service';
import { isPlatformBrowser } from '@angular/common';
import { NetworkStatusService } from 'src/app/Shared/_services/network-status/network-status.service';
import { ErrorStateService } from 'src/app/Shared/_services/error-state/error-state.service';

@Injectable()
export class ResponseCheckInterceptor implements HttpInterceptor {
  constructor(
    private storage: StorageService,
    private toastService: ToastService,
    private auth: AuthService,
    private router: Router,
    private nav: Navigation,
    @Inject(PLATFORM_ID) private platformId: Object,
    private networkStatusService: NetworkStatusService,
    private errorStateService: ErrorStateService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      // Retry for retryable errors (network + gateway errors)
      retry({
        count: 2,
        delay: (error, retryCount) => {
          if (this.isRetryableError(error)) {
            return timer(1000 * retryCount); // 1s, 2s delay
          }
          return throwError(() => error);
        },
      }),
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // Your existing metadata logic
          if (
            this.storage.timezone_offset_with_dst !=
            event?.body?.metdata?.data_from_ip?.timezone_offset_with_dst
          ) {
            if (event?.body?.metdata) {
              this.storage.setMetaData(event.body.metdata);
            }
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.toastService.clear();

        // Queue retryable errors (network errors + transient server errors)
        if (this.isRetryableError(error)) {
          this.handleNetworkError(req);
        } else {
          this.handleServerError(error);
        }
        return throwError(() => error);
      })
    );
  }

  // Check if error is retryable (network errors + transient server errors)
  private isRetryableError(error: HttpErrorResponse): boolean {
    const isOffline = isPlatformBrowser(this.platformId)
      ? !navigator.onLine
      : false;
    // Retry on: no response (status 0), offline, or gateway errors (502, 503, 504)
    return (
      error.status === 0 || isOffline || [502, 503, 504].includes(error.status)
    );
  }

  // Simple network error handling with request queueing
  private handleNetworkError(request: HttpRequest<any>): void {
    // Don't re-queue retry requests (they're already being retried by NetworkStatusService)
    if (request.headers.has('X-Retry-Request')) {
      return;
    }
    // Queue the failed request for retry when back online
    // The offline banner component handles visual feedback
    this.networkStatusService.queueRequest(request);
  }

  // Server error handling - Uses inline error state for specific status codes only
  // Only 400, 401, 403, 404, 500 show inline error status
  // Other errors (422, etc.) are handled by the calling component
  private handleServerError(error: HttpErrorResponse): void {
    const url = error.url || undefined;
    const errorBody = error.error;

    switch (error.status) {
      case 400:
        // Bad Request - show inline error
        this.errorStateService.setError(
          error.status,
          error.statusText || 'Bad Request',
          errorBody,
          url
        );
        break;
      case 401:
        // Unauthorized - logout and show inline error
        this.errorStateService.setError(
          error.status,
          error.statusText || 'Unauthorized',
          errorBody,
          url
        );
        this.auth.logout();
        break;
      case 403:
        // Forbidden - logout and show inline error
        this.errorStateService.setError(
          error.status,
          error.statusText || 'Forbidden',
          errorBody,
          url
        );
        this.auth.logout();
        break;
      case 404:
        // Not Found - show inline error (no redirect)
        this.errorStateService.setError(
          error.status,
          error.statusText || 'Not Found',
          errorBody,
          url
        );
        break;
      case 500:
        // Internal Server Error - show inline error (no redirect)
        this.errorStateService.setError(
          error.status,
          error.statusText || 'Internal Server Error',
          errorBody,
          url
        );
        break;
      case 422:
        // Validation errors - show toast message, no inline error
        // Let the component handle field-level validation display
        this.showErrorToast(errorBody);
        break;
      // All other status codes (502, 503, etc.) are NOT shown as inline errors
      // They are passed through for the calling component to handle
      default:
        // No inline error display for other status codes
        break;
    }
  }

  /**
   * Show error toast for validation errors (422)
   * Extracts error messages from response body - only from 'error' field, ignoring 'message'
   */
  private showErrorToast(errorBody: any): void {
    if (!errorBody) return;

    // Only extract from error field - completely ignore message field for 422 errors
    // The 'error' field contains the actual validation errors
    if (errorBody.error) {
      // Array format: { error: ["msg1", "msg2"] }
      if (Array.isArray(errorBody.error)) {
        errorBody.error.forEach((msg: string) => {
          this.toastService.showError(msg);
        });
        return;
      }
      // Object format: { error: { field: ["msg1"] } }
      if (typeof errorBody.error === 'object') {
        Object.values(errorBody.error).forEach((fieldErrors: any) => {
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach((msg: string) => {
              this.toastService.showError(msg);
            });
          }
        });
        return;
      }
    }

    // Fallback to errors (plural) if error doesn't exist
    if (errorBody.errors && Array.isArray(errorBody.errors)) {
      errorBody.errors.forEach((msg: string) => {
        this.toastService.showError(msg);
      });
      return;
    }

    // Fallback to Error (capital E) if others don't exist
    if (errorBody.Error && Array.isArray(errorBody.Error)) {
      errorBody.Error.forEach((msg: string) => {
        this.toastService.showError(msg);
      });
      return;
    }

    // NOTE: 'message' field is intentionally NOT used for 422 errors
    // It's a generic message that duplicates specific error messages
  }
}
